import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiCamera, FiEdit3, FiPlus } from 'react-icons/fi';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { useApi } from '../hooks/useApi';
import { sanitizeText, sanitizeNumber } from '../utils/sanitize';
import { splitIva, ALICUOTAS_IVA } from '../utils/iva';
import { formatMoney } from '../utils/money';
import { FormAlert, FormShell, FormField, FormActions, formInputClass } from '../components/form';
import { useThemeClasses } from '../hooks/useThemeClasses';
import CurrencyFxFields from '../components/CurrencyFxFields';
import { monedaOptionsForUser } from '../utils/multimoneda';
import { downloadTabularExport } from '../utils/downloadExport';
import MoneyDisplay from '../components/MoneyDisplay';
import CurrencyBadge from '../components/CurrencyBadge';
import TotalsByCurrency from '../components/TotalsByCurrency';
import ExpenseCsvImport from '../components/ExpenseCsvImport';
import ExpenseOcrCapture from '../components/ExpenseOcrCapture';
import BankReconciliation from '../components/BankReconciliation';
import { canAccessProfesionalFeatures } from '../utils/profesionalAccess';
import { canAccessOcr } from '../utils/ocrAccess';
import RecurringList from '../components/RecurringList';
import { defaultNextRun, normalizeRecurringFrequency, runDueRecurring } from '../utils/recurring';
import { cloneExpenseForForm } from '../utils/duplicar';
import OnboardingContinueAction from '../components/OnboardingContinueAction';
import PlanUsageBanner from '../components/PlanUsageBanner';
import { isOperationalWriteDisabled } from '../utils/writeAccess';

const CATEGORIAS = ['', 'Insumos', 'Alquiler', 'Servicios', 'Impuestos', 'Sueldos', 'Otro'];

const isMonotributoAuto = (g) => Boolean(g?.monotributoAuto || g?.metadata?.source === 'monotributo_auto');

const isExpensePending = (g) => g?.pagado === false;

const showsEstadoPago = (g) => isMonotributoAuto(g) || isExpensePending(g);

const expenseMesKey = (g) => g?.mesImputacion || String(g?.fecha || '').slice(0, 7);

const formInicial = {
  fecha: new Date().toISOString().split('T')[0],
  concepto: '',
  monto: 0,
  moneda: 'ARS',
  tipoCambio: '',
  categoria: '',
  proveedor: '',
  cliente: '',
  notas: '',
  alicuotaIva: 0,
  pagado: true,
  recurrente: false,
  frecuenciaRecurrente: 'monthly',
  proximaFecha: '',
};

function formatFecha(fecha) {
  if (!fecha) return '—';
  const s = String(fecha).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return '—';
  return new Date(y, m - 1, d).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function mesActualKey() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

const Egresos = () => {
  const location = useLocation();
  const { token, user } = useContext(AuthContext);
  const monedaOptions = useMemo(() => monedaOptionsForUser(user), [user]);
  const { modoOscuro } = useContext(ThemeContext);
  const tc = useThemeClasses();
  const { secureFetch } = useApi();
  const hasProfesional = canAccessProfesionalFeatures(user);
  const hasOcr = canAccessOcr(user);
  const readOnly = isOperationalWriteDisabled(user);
  const [ocrUsage, setOcrUsage] = useState(null);
  const [visionEnabled, setVisionEnabled] = useState(false);
  const [ocrScanEnabled, setOcrScanEnabled] = useState(false);
  const ocrBetaPaused = hasOcr && !ocrScanEnabled;

  const tabParam = new URLSearchParams(location.search).get('tab');
  const resolveInitialTab = () => {
    if (['crear', 'ticket', 'cargar'].includes(tabParam)) return 'cargar';
    if (['importar', 'conciliar', 'recurrentes'].includes(tabParam)) return tabParam;
    return 'listado';
  };

  const [egresos, setEgresos] = useState([]);
  const [tab, setTab] = useState(resolveInitialTab);
  const [editId, setEditId] = useState(null);
  const [fromOcr, setFromOcr] = useState(false);
  const [form, setForm] = useState(formInicial);
  const formRef = useRef(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [exportingCsv, setExportingCsv] = useState(false);
  const [clientesOpciones, setClientesOpciones] = useState([]);

  const fetchOcrUsage = useCallback(async () => {
    if (!token || !hasOcr) return;
    try {
      const res = await secureFetch('/api/ocr/usage');
      if (!res.ok) return;
      const data = await res.json();
      setOcrUsage(data.usage || null);
      setVisionEnabled(Boolean(data.visionEnabled));
      setOcrScanEnabled(data.scanEnabled === true);
    } catch {
      setOcrUsage(null);
    }
  }, [token, secureFetch, hasOcr]);

  const fetchClientes = useCallback(async () => {
    if (!token) return;
    try {
      const res = await secureFetch('/api/clients');
      if (!res.ok) return;
      const data = await res.json();
      const names = (Array.isArray(data) ? data : [])
        .map((c) => c.razonSocial || c.name || c.nombre || '')
        .filter(Boolean);
      setClientesOpciones([...new Set(names)].sort((a, b) => a.localeCompare(b, 'es')));
    } catch {
      setClientesOpciones([]);
    }
  }, [token, secureFetch]);

  const fetchEgresos = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await secureFetch('/api/expenses');
      if (!res.ok) throw new Error('Error al obtener gastos');
      const data = await res.json();
      setEgresos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [token, secureFetch]);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  useEffect(() => {
    fetchOcrUsage();
  }, [fetchOcrUsage]);

  useEffect(() => {
    if (!token) return;
    if (readOnly) return;
    let cancelled = false;
    (async () => {
      const due = await runDueRecurring(secureFetch);
      if (cancelled) return;
      await fetchEgresos();
      if (due.generated > 0) {
        setSuccess(due.generated === 1
          ? 'Se cargó 1 gasto recurrente.'
          : `Se cargaron ${due.generated} gastos recurrentes.`);
      }
    })();
    return () => { cancelled = true; };
  }, [token, fetchEgresos, secureFetch, readOnly]);

  useEffect(() => {
    if (readOnly && tab !== 'listado') {
      setTab('listado');
      setEditId(null);
      setForm(formInicial);
      setFromOcr(false);
    }
  }, [readOnly, tab]);

  const egresosFiltrados = useMemo(() => {
    let filtradas = egresos;
    if (filtroCategoria) filtradas = filtradas.filter((e) => (e.categoria || '') === filtroCategoria);
    if (filtroFechaDesde) {
      filtradas = filtradas.filter((e) => String(e.fecha || '').slice(0, 10) >= filtroFechaDesde);
    }
    if (filtroFechaHasta) {
      filtradas = filtradas.filter((e) => String(e.fecha || '').slice(0, 10) <= filtroFechaHasta);
    }
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      filtradas = filtradas.filter(
        (e) =>
          (e.concepto || '').toLowerCase().includes(q) ||
          (e.proveedor || '').toLowerCase().includes(q) ||
          (e.cliente || '').toLowerCase().includes(q) ||
          (e.notas || '').toLowerCase().includes(q)
      );
    }
    return filtradas.slice().sort((a, b) => String(b.fecha || '').localeCompare(String(a.fecha || '')));
  }, [egresos, filtroCategoria, filtroFechaDesde, filtroFechaHasta, busqueda]);

  const totalesPorMoneda = useMemo(() => {
    const map = {};
    egresosFiltrados.forEach((e) => {
      const m = (e.moneda || 'ARS').toUpperCase();
      map[m] = (map[m] || 0) + (Number(e.monto) || 0);
    });
    return map;
  }, [egresosFiltrados]);

  const totalEquivalenteArs = useMemo(
    () => egresosFiltrados.reduce((sum, e) => {
      const m = (e.moneda || 'ARS').toUpperCase();
      if (m === 'ARS') return sum + (Number(e.monto) || 0);
      if (e.montoArs != null) return sum + Number(e.montoArs);
      if (e.tipoCambio && Number(e.tipoCambio) > 0) return sum + (Number(e.monto) || 0) * Number(e.tipoCambio);
      return sum;
    }, 0),
    [egresosFiltrados]
  );

  const kpis = useMemo(() => {
    const key = mesActualKey();
    const delMes = egresos.filter((e) => expenseMesKey(e) === key);
    const mesArs = delMes.reduce((sum, e) => {
      const m = (e.moneda || 'ARS').toUpperCase();
      if (m === 'ARS') return sum + (Number(e.monto) || 0);
      if (e.montoArs != null) return sum + Number(e.montoArs);
      return sum;
    }, 0);
    const cats = new Set(egresos.map((e) => e.categoria).filter(Boolean));
    return {
      cantidad: egresosFiltrados.length,
      totalArs: totalEquivalenteArs,
      categorias: cats.size,
      mesArs,
    };
  }, [egresos, egresosFiltrados.length, totalEquivalenteArs]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let sanitizedValue = value;
    if (name === 'monto') {
      sanitizedValue = parseFloat(value) || 0;
      if (sanitizedValue < 0) sanitizedValue = 0;
      if (sanitizedValue > 999999999) sanitizedValue = 999999999;
    } else if (name === 'concepto' || name === 'proveedor' || name === 'cliente' || name === 'notas') {
      sanitizedValue = sanitizeText(value);
    }
    setForm((prev) => ({ ...prev, [name]: sanitizedValue }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.concepto?.trim()) {
      setError('El concepto es obligatorio.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const monto = sanitizeNumber(form.monto);
      const alicuotaIva = Number(form.alicuotaIva) || 0;
      const split = splitIva({
        importe: monto,
        alicuota: alicuotaIva,
        tipo: alicuotaIva > 0 ? 'B' : 'C'
      });
      const moneda = ['ARS', 'USD', 'EUR'].includes(form.moneda) ? form.moneda : 'ARS';
      const sanitizedData = {
        fecha: form.fecha,
        concepto: sanitizeText(form.concepto),
        monto,
        moneda,
        tipoCambio: moneda === 'ARS' ? null : (Number(form.tipoCambio) || null),
        categoria: CATEGORIAS.includes(form.categoria) ? form.categoria : '',
        proveedor: sanitizeText(form.proveedor),
        cliente: sanitizeText(form.cliente),
        notas: sanitizeText(form.notas),
        alicuotaIva,
        iva: split.iva,
        neto: split.neto,
        pagado: form.pagado !== false,
      };

      const url = editId ? `/api/expenses/${editId}` : '/api/expenses';
      const method = editId ? 'PUT' : 'POST';
      const res = await secureFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sanitizedData)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al guardar');
      setSuccess(editId ? 'Gasto actualizado correctamente.' : 'Gasto registrado correctamente.');
      if (!editId && form.recurrente) {
        try {
          const recRes = await secureFetch('/api/recurring', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tipo: 'egreso',
              frecuencia: normalizeRecurringFrequency(form.frecuenciaRecurrente),
              nextRunDate: form.proximaFecha || defaultNextRun(form.fecha, form.frecuenciaRecurrente),
              payload: sanitizedData,
            }),
          });
          if (recRes.ok) {
            const freq = normalizeRecurringFrequency(form.frecuenciaRecurrente);
            setSuccess(
              freq === 'yearly'
                ? 'Gasto registrado y recordatorio anual creado (dominio, hosting, etc.).'
                : 'Gasto registrado y plantilla mensual creada.'
            );
          }
        } catch {
          // el gasto ya se guardó
        }
      }
      setForm(formInicial);
      setEditId(null);
      setFromOcr(false);
      fetchEgresos();
      setTab('listado');
    } catch (err) {
      setError(sanitizeText(err.message) || 'Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicar = (gasto) => {
    if (readOnly) return;
    setEditId(null);
    setFromOcr(false);
    setForm(cloneExpenseForForm(gasto));
    setTab('cargar');
    setError('');
    setSuccess('Revisá la copia y guardá. La fecha queda en hoy.');
  };

  const handleDuplicarUltimo = () => {
    if (!egresos.length) return;
    const ultimo = [...egresos].sort((a, b) => String(b.fecha || '').localeCompare(String(a.fecha || '')))[0];
    handleDuplicar(ultimo);
  };

  const handleEditar = (gasto) => {
    if (readOnly) return;
    setEditId(gasto._id);
    setFromOcr(false);
    setForm({
      fecha: gasto.fecha || formInicial.fecha,
      concepto: gasto.concepto || '',
      monto: gasto.monto ?? 0,
      moneda: gasto.moneda || 'ARS',
      tipoCambio: gasto.tipoCambio ?? '',
      categoria: gasto.categoria || '',
      proveedor: gasto.proveedor || '',
      cliente: gasto.cliente || '',
      notas: gasto.notas || '',
      alicuotaIva: gasto.alicuotaIva || 0,
      pagado: gasto.pagado !== false,
    });
    setTab('cargar');
    setError('');
    setSuccess('');
  };

  const goToCargar = (manual = false) => {
    if (readOnly) return;
    setTab('cargar');
    setEditId(null);
    if (manual) {
      setFromOcr(false);
      setForm(formInicial);
    }
    setError('');
    setSuccess('');
  };

  const handleOcrApply = (fields) => {
    setForm((p) => ({
      ...p,
      ...fields,
      monto: fields.monto || 0,
      proveedor: fields.proveedor || p.proveedor,
    }));
    setEditId(null);
    setFromOcr(true);
    setSuccess('Datos del comprobante cargados. Revisá los campos y tocá Registrar gasto.');
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  };

  const switchTab = (nextTab) => {
    if (readOnly && nextTab !== 'listado') return;
    setTab(nextTab);
    if (nextTab !== 'cargar') {
      setEditId(null);
      setForm(formInicial);
      setFromOcr(false);
    } else if (!editId && tab !== 'cargar') {
      setForm(formInicial);
      setFromOcr(false);
    }
    setError('');
    if (nextTab !== 'listado') setSuccess('');
  };

  const exportarEgresosExcel = async () => {
    setExportingCsv(true);
    setError('');
    try {
      const name = await downloadTabularExport(
        secureFetch,
        '/api/export/egresos',
        'contsuite-egresos.xlsx'
      );
      setSuccess(`Exportación descargada (${name}).`);
    } catch (err) {
      setError(err.message || 'Error al exportar');
    } finally {
      setExportingCsv(false);
    }
  };

  const handleTogglePagado = async (g) => {
    if (readOnly || !g?._id) return;
    const nextPagado = isExpensePending(g);
    setError('');
    try {
      const res = await secureFetch(`/api/expenses/${g._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...g,
          pagado: nextPagado,
          metadata: { ...(g.metadata || {}), pagado: nextPagado },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al actualizar');
      setSuccess(
        nextPagado
          ? (isMonotributoAuto(g) ? 'Cuota Monotributo marcada como pagada.' : 'Gasto marcado como pagado.')
          : (isMonotributoAuto(g) ? 'Cuota Monotributo marcada como pendiente.' : 'Gasto marcado como pendiente de pago.')
      );
      fetchEgresos();
    } catch (err) {
      setError(err.message || 'No se pudo actualizar el estado de pago');
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await secureFetch(`/api/expenses/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al eliminar');
      setSuccess('Gasto eliminado.');
      if (editId === id) setEditId(null);
      fetchEgresos();
    } catch (err) {
      setError(err.message || 'Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const ivaSplit = Number(form.alicuotaIva) > 0 && Number(form.monto) > 0
    ? splitIva({ importe: Number(form.monto) || 0, alicuota: Number(form.alicuotaIva), tipo: 'B' })
    : null;

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const tabInactive = modoOscuro ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50';
  const btnOut = modoOscuro ? 'border-gray-600 text-white hover:bg-gray-700' : 'border-gray-300 text-gray-900 hover:bg-gray-50';
  const divide = modoOscuro ? 'border-gray-700' : 'border-gray-200';
  const inputBg = modoOscuro
    ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500'
    : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400';
  const heroBg = modoOscuro
    ? 'border-[#1F80FF]/30 bg-gradient-to-br from-gray-900 via-gray-900 to-blue-950/40'
    : 'border-[#1F80FF]/20 bg-gradient-to-br from-white via-white to-blue-50/60';
  const ocrFieldRing = fromOcr
    ? (modoOscuro ? 'ring-1 ring-[#1F80FF]/50 border-[#1F80FF]/40' : 'ring-1 ring-[#1F80FF]/30 border-[#1F80FF]/40')
    : '';

  const primaryTabs = readOnly
    ? [{ id: 'listado', label: 'Mis gastos' }]
    : [
        { id: 'listado', label: 'Mis gastos' },
        { id: 'cargar', label: editId ? 'Editar gasto' : 'Cargar gasto' },
        { id: 'recurrentes', label: 'Recurrentes' },
      ];
  const toolTabs = readOnly || !hasProfesional
    ? []
    : [{ id: 'importar', label: 'Importar CSV' }, { id: 'conciliar', label: 'Conciliar banco' }];

  const ocrUsageHint = ocrUsage
    ? ocrUsage.trialOnly
      ? `${ocrUsage.trialRemaining ?? 0} escaneo(s) de prueba`
      : `${ocrUsage.remaining ?? 0} escaneos disponibles este mes`
    : null;

  return (
    <div className={modoOscuro ? 'w-full min-h-[calc(100dvh-4rem)] bg-black text-white' : 'w-full min-h-[calc(100dvh-4rem)] bg-[#f5f5f7] text-gray-900'}>
      <div className="w-full max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12 py-3 pb-3 space-y-3">
        <header className="min-w-0">
          <h1 className={`text-xl sm:text-2xl font-semibold leading-tight ${textPri}`}>Gastos</h1>
          <p className={`text-xs sm:text-sm mt-0.5 ${textSec}`}>
            Escaneá un comprobante o cargá el gasto a mano. Categorías, moneda e IVA.
          </p>
        </header>

        {hasOcr && tab === 'cargar' && (
          <PlanUsageBanner modoOscuro={modoOscuro} showOcr compact />
        )}

        <FormAlert
          type="success"
          className="mb-2"
          action={
            <OnboardingContinueAction
              stepId="expense"
              visible={Boolean(success && /registrado|generado desde la plantilla/i.test(success))}
              modoOscuro={modoOscuro}
            />
          }
        >
          {success ? sanitizeText(success) : null}
        </FormAlert>
        <FormAlert type="error" className="mb-2">{error ? sanitizeText(error) : null}</FormAlert>

        <div className="space-y-1.5">
          <div className={`rounded-xl border p-0.5 ${box}`}>
            <div className="flex gap-1">
              {primaryTabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => switchTab(t.id)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                    tab === t.id ? 'bg-[#1F80FF] text-white' : tabInactive
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {toolTabs.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {toolTabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => switchTab(t.id)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border ${
                    tab === t.id
                      ? 'bg-[#1F80FF] text-white border-[#1F80FF]'
                      : `${btnOut} border`
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {tab === 'listado' && hasOcr && !readOnly && (
          <section className={`rounded-2xl border p-4 sm:p-5 ${heroBg}`}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className={`text-[11px] font-semibold uppercase tracking-wide ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                  Forma más rápida
                </p>
                <h2 className={`text-base sm:text-lg font-semibold mt-1 ${textPri}`}>
                  ¿Tenés un ticket o factura?
                </h2>
                <p className={`text-xs sm:text-sm mt-1 ${textSec}`}>
                  Escaneá el ticket y prellenamos monto, fecha y concepto. Vos revisás y guardás.
                </p>
                {ocrBetaPaused && (
                  <p className={`text-xs mt-2 font-medium ${modoOscuro ? 'text-amber-300' : 'text-amber-800'}`}>
                    Escaneo de comprobantes: próximamente en la beta. Usá &quot;Cargar a mano&quot;.
                  </p>
                )}
                {!ocrBetaPaused && (ocrUsageHint || visionEnabled) && (
                  <p className={`text-xs mt-2 font-medium ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                    {visionEnabled && <span>Lectura con Google Vision activa · </span>}
                    {ocrUsageHint}
                    {ocrUsage?.remaining === 0 && (
                      <Link to="/uso-plan#ocr-packs" className="ml-1 underline">Comprar más</Link>
                    )}
                  </p>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => goToCargar(false)}
                  className={`inline-flex flex-col items-center justify-center gap-0.5 px-4 py-2.5 rounded-xl text-sm font-semibold ${
                    ocrBetaPaused
                      ? 'bg-[#1F80FF]/50 text-white/90 cursor-default'
                      : 'bg-[#1F80FF] text-white hover:bg-[#004AAD]'
                  }`}
                  aria-disabled={ocrBetaPaused}
                >
                  <span className="inline-flex items-center gap-2">
                    <FiCamera className="w-4 h-4" />
                    Escanear comprobante
                  </span>
                  {ocrBetaPaused && <span className="text-[10px] font-medium opacity-90">Próximamente</span>}
                </button>
                <button
                  type="button"
                  onClick={() => goToCargar(true)}
                  className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border ${btnOut}`}
                >
                  <FiEdit3 className="w-4 h-4" />
                  Cargar a mano
                </button>
              </div>
            </div>
          </section>
        )}

        {tab === 'cargar' && (
          <div className="space-y-4">
            {hasOcr && !editId && (
              <ExpenseOcrCapture
                embedded
                modoOscuro={modoOscuro}
                usage={ocrUsage}
                betaPaused={ocrBetaPaused}
                onUsageChange={setOcrUsage}
                onApply={handleOcrApply}
              />
            )}

            {hasOcr && !editId && (
              <div className="relative py-1">
                <div className={`absolute inset-0 flex items-center`} aria-hidden="true">
                  <div className={`w-full border-t ${divide}`} />
                </div>
                <div className="relative flex justify-center">
                  <span className={`px-3 text-xs font-medium ${modoOscuro ? 'bg-black text-gray-400' : 'bg-[#f5f5f7] text-gray-500'}`}>
                    O completá el gasto manualmente
                  </span>
                </div>
              </div>
            )}

            {fromOcr && (
              <div className={`rounded-xl border px-3 py-2.5 text-xs sm:text-sm ${modoOscuro ? 'border-[#1F80FF]/40 bg-[#1F80FF]/10 text-blue-100' : 'border-[#1F80FF]/30 bg-blue-50 text-blue-900'}`}>
                <strong>Datos detectados del comprobante.</strong> Revisá monto, fecha y concepto antes de guardar.
              </div>
            )}

            <div ref={formRef}>
          <FormShell
            box={box}
            divide={divide}
            textPri={textPri}
            textMuted={textMuted}
            title={editId ? 'Editar gasto' : fromOcr ? 'Completá y guardá el gasto' : 'Cargar gasto manualmente'}
            subtitle={editId ? 'Fecha, concepto e importe son obligatorios.' : 'Completá los campos. Fecha, concepto e importe son obligatorios.'}
            headerClassName="px-4 py-2.5"
            bodyClassName="p-4"
            titleClassName="text-sm"
            iconWrapClassName="w-8 h-8"
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
              </svg>
            }
          >
            <form onSubmit={handleSubmit} className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2.5">
                <FormField label="Fecha" required labelClass={tc.labelClass}>
                  <input type="date" name="fecha" value={form.fecha} onChange={(e) => { handleChange(e); setFromOcr(false); }} required className={`${formInputClass(tc.inputClass, { compact: true })} ${fromOcr ? ocrFieldRing : ''}`} />
                </FormField>
                <FormField label="Concepto" required labelClass={tc.labelClass} className="sm:col-span-2 lg:col-span-2">
                  <input type="text" name="concepto" value={form.concepto} onChange={(e) => { handleChange(e); setFromOcr(false); }} placeholder="Alquiler, servicios, insumos…" required className={`${formInputClass(tc.inputClass, { compact: true })} ${fromOcr ? ocrFieldRing : ''}`} />
                </FormField>
                <FormField label="Monto" required labelClass={tc.labelClass}>
                  <input type="number" name="monto" value={form.monto || ''} onChange={(e) => { handleChange(e); setFromOcr(false); }} min="0" step="0.01" required className={`${formInputClass(tc.inputClass, { compact: true })} ${fromOcr ? ocrFieldRing : ''}`} />
                </FormField>
                <FormField label="Moneda" labelClass={tc.labelClass}>
                  <select
                    name="moneda"
                    value={form.moneda}
                    onChange={(e) => {
                      handleChange(e);
                      if (e.target.value === 'ARS') setForm((p) => ({ ...p, tipoCambio: '' }));
                    }}
                    className={formInputClass(tc.inputClass, { compact: true })}
                  >
                    {monedaOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Categoría" labelClass={tc.labelClass}>
                  <select name="categoria" value={form.categoria} onChange={handleChange} className={formInputClass(tc.inputClass, { compact: true })}>
                    {CATEGORIAS.map((cat) => (
                      <option key={cat || 'vacio'} value={cat}>{cat || 'Sin categoría'}</option>
                    ))}
                  </select>
                </FormField>
                <FormField label="IVA incluido" labelClass={tc.labelClass}>
                  <select name="alicuotaIva" value={form.alicuotaIva || 0} onChange={handleChange} className={formInputClass(tc.inputClass, { compact: true })}>
                    <option value={0}>Sin IVA</option>
                    {ALICUOTAS_IVA.filter((a) => a > 0).map((a) => (
                      <option key={a} value={a}>{String(a).replace('.', ',')}% incluido</option>
                    ))}
                  </select>
                  {ivaSplit && (
                    <p className={`text-[11px] mt-1 ${textMuted}`}>
                      Neto {ivaSplit.neto.toLocaleString('es-AR', { minimumFractionDigits: 2 })} · IVA {ivaSplit.iva.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </p>
                  )}
                </FormField>
                <FormField label="Proveedor" labelClass={tc.labelClass}>
                  <input type="text" name="proveedor" value={form.proveedor} onChange={handleChange} placeholder="Nombre del proveedor" className={formInputClass(tc.inputClass, { compact: true })} />
                </FormField>
                <FormField
                  label="Cliente (opcional)"
                  labelClass={tc.labelClass}
                  hint="Asigná el gasto a un cliente para calcular margen real en Reportes"
                  textMuted={textMuted}
                >
                  <input
                    type="text"
                    name="cliente"
                    list="egresos-clientes-datalist"
                    value={form.cliente}
                    onChange={handleChange}
                    placeholder="Asignar costo a un cliente"
                    className={formInputClass(tc.inputClass, { compact: true })}
                  />
                  <datalist id="egresos-clientes-datalist">
                    {clientesOpciones.map((nombre) => (
                      <option key={nombre} value={nombre} />
                    ))}
                  </datalist>
                </FormField>
                <FormField label="Notas" labelClass={tc.labelClass} className="sm:col-span-2 lg:col-span-3">
                  <input type="text" name="notas" value={form.notas} onChange={handleChange} className={formInputClass(tc.inputClass, { compact: true })} placeholder="Observaciones (opcional)" />
                </FormField>
                <FormField
                  label="Estado de pago"
                  labelClass={tc.labelClass}
                  hint="Pendiente: el gasto cuenta en el mes pero aún no lo pagaste."
                  textMuted={textMuted}
                >
                  <select
                    name="pagado"
                    value={form.pagado !== false ? 'pagado' : 'pendiente'}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, pagado: e.target.value === 'pagado' }));
                      setError('');
                      setSuccess('');
                    }}
                    className={formInputClass(tc.inputClass, { compact: true })}
                  >
                    <option value="pagado">Pagado</option>
                    <option value="pendiente">Pendiente de pago</option>
                  </select>
                </FormField>
                {!editId && (
                  <FormField
                    label="Repetir"
                    labelClass={tc.labelClass}
                    className="sm:col-span-2"
                    hint="Para dominio, hosting u otros pagos una vez al año, elegí «Cada año»."
                    textMuted={textMuted}
                  >
                    <label className={`flex items-center gap-2 text-sm ${textPri}`}>
                      <input
                        type="checkbox"
                        name="recurrente"
                        checked={!!form.recurrente}
                        onChange={(e) => setForm((p) => ({ ...p, recurrente: e.target.checked }))}
                      />
                      Crear plantilla recurrente
                    </label>
                    {form.recurrente && (
                      <div className="mt-2 space-y-2">
                        <select
                          name="frecuenciaRecurrente"
                          value={form.frecuenciaRecurrente || 'monthly'}
                          onChange={(e) => {
                            const frecuencia = e.target.value;
                            setForm((p) => ({
                              ...p,
                              frecuenciaRecurrente: frecuencia,
                              proximaFecha: defaultNextRun(p.fecha, frecuencia),
                            }));
                          }}
                          className={formInputClass(tc.inputClass, { compact: true })}
                        >
                          <option value="monthly">Cada mes</option>
                          <option value="yearly">Cada año</option>
                        </select>
                        <div>
                          <label className={`block text-[11px] mb-1 ${textMuted}`}>
                            Próximo vencimiento (se genera el gasto ese día)
                          </label>
                          <input
                            type="date"
                            name="proximaFecha"
                            value={form.proximaFecha || defaultNextRun(form.fecha, form.frecuenciaRecurrente)}
                            onChange={handleChange}
                            className={formInputClass(tc.inputClass, { compact: true })}
                          />
                        </div>
                      </div>
                    )}
                  </FormField>
                )}
              </div>

              <CurrencyFxFields
                moneda={form.moneda}
                monto={form.monto}
                tipoCambio={form.tipoCambio}
                onTipoCambioChange={(val) => setForm((p) => ({ ...p, tipoCambio: val }))}
                labelClass={tc.labelClass}
                textMuted={textMuted}
                tc={tc}
              />

              <FormActions
                submitLabel={editId ? 'Actualizar gasto' : fromOcr ? 'Confirmar y registrar gasto' : 'Registrar gasto'}
                onCancel={() => { switchTab('listado'); }}
                loading={loading}
                btnPrimary={tc.btnPrimary}
                btnOutline={tc.btnOutline}
              />
            </form>
          </FormShell>
            </div>
          </div>
        )}

        {tab === 'recurrentes' && (
          <RecurringList
            tipo="egreso"
            modoOscuro={modoOscuro}
            onGenerated={() => {
              setSuccess('Gasto generado desde la plantilla.');
              fetchEgresos();
            }}
          />
        )}
        {tab === 'importar' && hasProfesional && (
          <ExpenseCsvImport
            modoOscuro={modoOscuro}
            onCancel={() => setTab('listado')}
            onImported={(data) => {
              setSuccess(data.message || 'Importación completada.');
              fetchEgresos();
              setTab('listado');
            }}
          />
        )}

        {tab === 'conciliar' && hasProfesional && (
          <BankReconciliation
            modoOscuro={modoOscuro}
            onCancel={() => setTab('listado')}
            onApplied={(data) => {
              setSuccess(data.message || 'Conciliación aplicada.');
              fetchEgresos();
              setTab('listado');
            }}
          />
        )}

        {tab === 'listado' && (
          <section className={`rounded-2xl border overflow-hidden ${box}`}>
              <div className="grid grid-cols-2 lg:grid-cols-4">
                {[
                  { label: 'Gastos', value: String(kpis.cantidad) },
                  { label: 'Total', value: formatMoney(kpis.totalArs, 'ARS') },
                  { label: 'Este mes', value: formatMoney(kpis.mesArs, 'ARS') },
                  { label: 'Categorías', value: String(kpis.categorias) },
                ].map((card, i) => (
                  <div
                    key={card.label}
                    className={`px-3 py-2 ${i > 0 ? (modoOscuro ? 'lg:border-l border-gray-800' : 'lg:border-l border-gray-100') : ''} ${i < 2 ? (modoOscuro ? 'border-b lg:border-b-0 border-gray-800' : 'border-b lg:border-b-0 border-gray-100') : ''}`}
                  >
                    <div className={`text-[10px] font-semibold tracking-wide uppercase ${textMuted}`}>{card.label}</div>
                    <div className={`text-sm font-semibold truncate ${textPri}`}>{card.value}</div>
                  </div>
                ))}
              </div>
              <div className={`px-3 py-2 border-t border-b space-y-2 ${divide}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h2 className={`text-sm font-semibold ${textPri}`}>Listado</h2>
                    <TotalsByCurrency
                      totals={totalesPorMoneda}
                      equivalenteArs={Object.keys(totalesPorMoneda).some((m) => m !== 'ARS') ? totalEquivalenteArs : null}
                      modoOscuro={modoOscuro}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    {!readOnly && (
                      <>
                    {hasOcr && (
                      <button
                        type="button"
                        onClick={() => goToCargar(false)}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold ${
                          ocrBetaPaused
                            ? 'bg-[#1F80FF]/50 text-white/90 cursor-default'
                            : 'bg-[#1F80FF] text-white hover:bg-[#1a6fe0]'
                        }`}
                        aria-disabled={ocrBetaPaused}
                      >
                        <FiCamera className="w-3.5 h-3.5" />
                        Escanear{ocrBetaPaused ? ' · próx.' : ''}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => goToCargar(true)}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold border ${hasOcr ? btnOut : 'bg-[#1F80FF] text-white border-[#1F80FF] hover:bg-[#1a6fe0]'}`}
                    >
                      <FiPlus className="w-3.5 h-3.5" />
                      {hasOcr ? 'Manual' : 'Nuevo'}
                    </button>
                    <button
                      type="button"
                      disabled={!egresos.length}
                      onClick={handleDuplicarUltimo}
                      className={`inline-flex items-center px-3 py-1.5 rounded-md border text-xs font-semibold disabled:opacity-50 ${btnOut}`}
                    >
                      Duplicar último
                    </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={exportarEgresosExcel}
                      disabled={exportingCsv}
                      className={`inline-flex items-center px-3 py-1.5 rounded-md border text-xs font-semibold disabled:opacity-50 ${btnOut}`}
                    >
                      {exportingCsv ? 'Exportando…' : 'Excel'}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                  <input
                    type="search"
                    placeholder="Buscar concepto, proveedor, cliente o notas"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className={`lg:col-span-2 px-3 py-1.5 rounded-lg border text-sm ${inputBg} focus:outline-none focus:ring-2 focus:ring-[#1F80FF]/30`}
                  />
                  <input
                    type="date"
                    value={filtroFechaDesde}
                    onChange={(e) => setFiltroFechaDesde(e.target.value)}
                    className={`px-3 py-1.5 rounded-lg border text-sm ${inputBg}`}
                    title="Desde"
                  />
                  <input
                    type="date"
                    value={filtroFechaHasta}
                    onChange={(e) => setFiltroFechaHasta(e.target.value)}
                    className={`px-3 py-1.5 rounded-lg border text-sm ${inputBg}`}
                    title="Hasta"
                  />
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => setFiltroCategoria('')}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${
                      !filtroCategoria ? 'bg-[#1F80FF] text-white' : modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600 border border-gray-200'
                    }`}
                  >
                    Todas
                  </button>
                  {CATEGORIAS.filter(Boolean).map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setFiltroCategoria(c === filtroCategoria ? '' : c)}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${
                        filtroCategoria === c ? 'bg-[#1F80FF] text-white' : modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600 border border-gray-200'
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                  {(filtroCategoria || filtroFechaDesde || filtroFechaHasta || busqueda) && (
                    <button
                      type="button"
                      onClick={() => { setFiltroCategoria(''); setFiltroFechaDesde(''); setFiltroFechaHasta(''); setBusqueda(''); }}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${btnOut} border`}
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </div>

              {loading && egresos.length === 0 ? (
                <div className="p-4 space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className={`h-14 rounded-xl animate-pulse ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`} />
                  ))}
                </div>
              ) : egresosFiltrados.length === 0 ? (
                <div className="px-4 py-10 text-center max-w-md mx-auto">
                  <p className={`text-sm font-medium ${textPri}`}>
                    {busqueda || filtroCategoria || filtroFechaDesde || filtroFechaHasta
                      ? 'No hay gastos en este recorte'
                      : 'Todavía no cargaste gastos'}
                  </p>
                  <p className={`text-xs mt-2 ${textMuted}`}>
                    {busqueda || filtroCategoria || filtroFechaDesde || filtroFechaHasta
                      ? 'Probá con otros filtros o limpiá la búsqueda.'
                      : hasOcr
                        ? ocrBetaPaused
                          ? 'Durante la beta cargá gastos a mano. El escaneo de comprobantes se habilitará pronto.'
                          : 'La forma más fácil: escaneá el ticket o factura y nosotros prellenamos los datos.'
                        : 'Registrá tu primer gasto con fecha, concepto e importe.'}
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
                    {!readOnly && (
                      <>
                    {hasOcr && !(busqueda || filtroCategoria || filtroFechaDesde || filtroFechaHasta) && (
                      <button
                        type="button"
                        onClick={() => goToCargar(false)}
                        className={`inline-flex flex-col items-center justify-center gap-0.5 px-4 py-2 rounded-xl text-sm font-semibold ${
                          ocrBetaPaused
                            ? 'bg-[#1F80FF]/50 text-white/90 cursor-default'
                            : 'bg-[#1F80FF] text-white'
                        }`}
                        aria-disabled={ocrBetaPaused}
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <FiCamera className="w-4 h-4" />
                          Escanear comprobante
                        </span>
                        {ocrBetaPaused && <span className="text-[10px] font-medium opacity-90">Próximamente</span>}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => goToCargar(true)}
                      className={`inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold ${
                        hasOcr && !(busqueda || filtroCategoria || filtroFechaDesde || filtroFechaHasta)
                          ? `border ${btnOut}`
                          : 'bg-[#1F80FF] text-white'
                      }`}
                    >
                      <FiEdit3 className="w-4 h-4" />
                      Cargar manualmente
                    </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-2 p-3 md:hidden">
                    {egresosFiltrados.map((g) => (
                      <div key={g._id} className={`rounded-xl border p-3 ${modoOscuro ? 'border-gray-800 bg-gray-800/40' : 'border-gray-100 bg-gray-50'}`}>
                        <div className="flex justify-between gap-2">
                          <div className="min-w-0">
                            <p className={`text-sm font-semibold truncate ${textPri}`}>{g.concepto}</p>
                            {showsEstadoPago(g) && (
                              <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                isExpensePending(g)
                                  ? (modoOscuro ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-100 text-amber-900')
                                  : (modoOscuro ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800')
                              }`}>
                                {isExpensePending(g) ? 'Pendiente de pago' : 'Pagado'}
                              </span>
                            )}
                            <p className={`text-[11px] ${textMuted}`}>
                              {formatFecha(g.fecha)}
                              {g.vencimiento && g.vencimiento !== String(g.fecha).slice(0, 10) ? ` · vence ${formatFecha(g.vencimiento)}` : ''}
                              {g.categoria ? ` · ${g.categoria}` : ''}
                              {g.proveedor ? ` · ${g.proveedor}` : ''}
                              {g.cliente ? ` · ${g.cliente}` : ''}
                            </p>
                          </div>
                          <MoneyDisplay
                            amount={g.monto}
                            currency={g.moneda || 'ARS'}
                            arsAmount={g.montoArs}
                            tipoCambio={g.tipoCambio}
                            modoOscuro={modoOscuro}
                            align="right"
                          />
                        </div>
                        {!readOnly && (
                        <div className="flex gap-2 mt-3 flex-wrap">
                          <button
                            type="button"
                            onClick={() => handleTogglePagado(g)}
                            className={`flex-1 min-w-[120px] py-1.5 rounded-md border text-[11px] font-semibold ${btnOut}`}
                          >
                            {isExpensePending(g) ? 'Marcar pagado' : 'Marcar pendiente'}
                          </button>
                          <button type="button" onClick={() => handleEditar(g)} className={`flex-1 py-1.5 rounded-md border text-[11px] font-semibold ${btnOut}`}>Editar</button>
                          <button type="button" onClick={() => handleDuplicar(g)} className={`flex-1 py-1.5 rounded-md border text-[11px] font-semibold ${btnOut}`}>Duplicar</button>
                          <button type="button" onClick={() => handleEliminar(g._id)} className={`flex-1 py-1.5 rounded-md border text-[11px] font-semibold ${btnOut}`}>Eliminar</button>
                        </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full min-w-[880px]">
                      <thead>
                        <tr className={`text-[11px] uppercase tracking-wide ${textMuted} ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
                          <th className="text-left font-semibold py-2.5 px-4">Fecha</th>
                          <th className="text-left font-semibold py-2.5 px-3">Concepto</th>
                          <th className="text-left font-semibold py-2.5 px-3">Categoría</th>
                          <th className="text-left font-semibold py-2.5 px-3">Proveedor</th>
                          <th className="text-left font-semibold py-2.5 px-3">Cliente</th>
                          <th className="text-center font-semibold py-2.5 px-3">Moneda</th>
                          <th className="text-right font-semibold py-2.5 px-3">Monto</th>
                          <th className="text-right font-semibold py-2.5 px-4">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {egresosFiltrados.map((g) => (
                          <tr key={g._id} className={`border-t ${modoOscuro ? 'border-gray-800 hover:bg-gray-800/70' : 'border-gray-100 hover:bg-gray-50'}`}>
                            <td className={`py-2.5 px-4 text-sm ${textSec}`}>{formatFecha(g.fecha)}</td>
                            <td className={`py-2.5 px-3 text-sm font-medium ${textPri}`}>
                              <div className="flex flex-wrap items-center gap-2">
                                <span>{g.concepto}</span>
                                {showsEstadoPago(g) && (
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                    isExpensePending(g)
                                      ? (modoOscuro ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-100 text-amber-900')
                                      : (modoOscuro ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800')
                                  }`}>
                                    {isExpensePending(g) ? 'Pendiente' : 'Pagado'}
                                  </span>
                                )}
                              </div>
                              {g.notas && <div className={`text-[11px] font-normal truncate max-w-[280px] ${textMuted}`}>{g.notas}</div>}
                            </td>
                            <td className={`py-2.5 px-3 text-sm ${textSec}`}>{g.categoria || '—'}</td>
                            <td className={`py-2.5 px-3 text-sm ${textSec}`}>{g.proveedor || '—'}</td>
                            <td className={`py-2.5 px-3 text-sm ${g.cliente ? textPri : textSec}`}>{g.cliente || '—'}</td>
                            <td className="py-2.5 px-3 text-center">
                              <CurrencyBadge currency={g.moneda || 'ARS'} modoOscuro={modoOscuro} size="xs" />
                            </td>
                            <td className="py-2.5 px-3">
                              <MoneyDisplay
                                amount={g.monto}
                                currency={g.moneda || 'ARS'}
                                arsAmount={g.montoArs}
                                tipoCambio={g.tipoCambio}
                                modoOscuro={modoOscuro}
                              />
                            </td>
                            <td className="py-2.5 px-4 text-right">
                              {!readOnly && (
                              <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                                <button
                                  type="button"
                                  onClick={() => handleTogglePagado(g)}
                                  className={`px-2.5 py-1 rounded-md border text-[11px] font-semibold ${btnOut}`}
                                >
                                  {isExpensePending(g) ? 'Pagado' : 'Pendiente'}
                                </button>
                                <button type="button" onClick={() => handleEditar(g)} className={`px-2.5 py-1 rounded-md border text-[11px] font-semibold ${btnOut}`}>Editar</button>
                                <button type="button" onClick={() => handleDuplicar(g)} className={`px-2.5 py-1 rounded-md border text-[11px] font-semibold ${btnOut}`}>Duplicar</button>
                                <button type="button" onClick={() => handleEliminar(g._id)} className={`px-2.5 py-1 rounded-md border text-[11px] font-semibold ${btnOut}`}>Eliminar</button>
                              </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </section>
        )}
      </div>
    </div>
  );
};

export default Egresos;
